/** SUNTRONIX ONE — Import History (assets + subsidiaries). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Download, RefreshCcw, FileSpreadsheet } from "lucide-react";

export default function ImportHistory() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/imports/");
      setItems(data.items || []);
    } catch { toast.error("Could not load import history"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const downloadErrors = async (id) => {
    try {
      const r = await api.get(`/imports/${id}/errors.csv`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = u; a.download = `import_${id}_errors.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
    } catch { toast.error("Could not download error report"); }
  };

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="import-history">
      <PageHeader
        eyebrow="Admin · Imports"
        title="Import history"
        context="Every bulk upload — devices and subsidiaries — with counts and downloadable error report."
        actions={<Button variant="outline" onClick={load}>
          <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>}
        testId="import-history-header"
      />

      <Section title={`${items.length} imports`} testId="import-history-section">
        <div className="st-panel overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-3 py-2">Kind</th>
                <th className="text-left px-3 py-2">File</th>
                <th className="text-left px-3 py-2">Uploaded</th>
                <th className="text-left px-3 py-2">By</th>
                <th className="text-right px-3 py-2">Rows</th>
                <th className="text-right px-3 py-2">Added</th>
                <th className="text-right px-3 py-2">Skipped</th>
                <th className="text-left px-3 py-2">Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={9} className="text-slate-400 text-center py-6">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={9} className="text-slate-500 text-center py-6">No imports yet.</td></tr>
              )}
              {items.map((s) => (
                <tr key={s.id} className="border-t border-slate-100"
                  data-testid={`import-row-${s.id}`}>
                  <td className="px-3 py-2">
                    <span className={"text-[11px] px-1.5 py-0.5 rounded border " +
                      (s.kind === "asset" ? "bg-sky-50 text-sky-700 border-sky-200"
                       : "bg-violet-50 text-violet-700 border-violet-200")}>
                      {s.kind}
                    </span>
                  </td>
                  <td className="px-3 py-2 flex items-center gap-1.5">
                    <FileSpreadsheet className="h-3.5 w-3.5 text-slate-400" />
                    {s.file_name}
                  </td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    {s.uploaded_at ? new Date(s.uploaded_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-3 py-2 text-[11px] text-slate-600">{s.uploaded_by}</td>
                  <td className="px-3 py-2 text-right font-mono">{s.counts?.total ?? "—"}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-700">{s.inserted_count ?? s.counts?.ready ?? 0}</td>
                  <td className="px-3 py-2 text-right font-mono text-red-700">{s.counts?.errors ?? 0}</td>
                  <td className="px-3 py-2">
                    <span className={"text-[11px] px-1.5 py-0.5 rounded border " +
                      (s.status === "COMPLETED"
                        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                        : s.status === "FAILED"
                          ? "bg-red-50 text-red-700 border-red-200"
                          : "bg-slate-50 text-slate-600 border-slate-200")}>
                      {s.status || "—"}
                    </span>
                  </td>
                  <td className="px-3 py-2 text-right">
                    {(s.counts?.errors || 0) > 0 && (
                      <Button size="sm" variant="ghost"
                        onClick={() => downloadErrors(s.id)}
                        data-testid={`import-errors-${s.id}`}>
                        <Download className="h-3 w-3 mr-1" /> Errors
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
    </div>
  );
}
